import {
  IconAdjustmentsHorizontal,
  IconArrowRight,
  IconBuildingStore,
  IconCategory,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {useCallback, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {getProviderLogo} from "@/components/Stories/stories.constants";
import css from "./GameSearchBar.module.css";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const GameSearchBar = ({
  onSearch,
  onSubmit,
  initialValue = "",
  providerOptions = [],
  categoryOptions = [],
  activeProvider = "",
  activeCategory = "",
  onProviderChange,
  onCategoryChange,
  resultCount,
  placeholder,
  hideProviderFilter = false,
}) => {
  const {t} = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(initialValue);
  const inputRef = useRef(null);

  const hasActiveFilters = activeProvider || activeCategory;

  const handleSearch = useCallback(
    e => {
      const value = e.target.value;
      setSearchValue(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && searchValue.trim()) {
        onSubmit?.(searchValue.trim());
      }
    },
    [onSubmit, searchValue]
  );

  const handleSubmit = useCallback(() => {
    if (searchValue.trim()) {
      onSubmit?.(searchValue.trim());
      onSearch?.(searchValue.trim());
    }
  }, [onSubmit, onSearch, searchValue]);

  const handleClear = useCallback(() => {
    setSearchValue("");
    onSearch?.("");
    inputRef.current?.focus();
  }, [onSearch]);

  const handleProviderClick = useCallback(
    provider => {
      onProviderChange?.(provider === activeProvider ? "" : provider);
      setFiltersOpen(false);
    },
    [onProviderChange, activeProvider]
  );

  const handleCategoryClick = useCallback(
    category => {
      onCategoryChange?.(category === activeCategory ? "" : category);
      setFiltersOpen(false);
    },
    [onCategoryChange, activeCategory]
  );

  const handleClearAll = useCallback(() => {
    onProviderChange?.("");
    onCategoryChange?.("");
  }, [onProviderChange, onCategoryChange]);

  const normalizedProviders = useMemo(
    () => providerOptions.map(opt => (typeof opt === "string" ? {value: opt, label: opt} : opt)),
    [providerOptions]
  );

  const normalizedCategories = useMemo(
    () => categoryOptions.map(opt => (typeof opt === "string" ? {value: opt, label: opt} : opt)),
    [categoryOptions]
  );

  const showFilters = (!hideProviderFilter && normalizedProviders.length > 0) || normalizedCategories.length > 0;

  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={css.toolbar}>
      {/* Search row */}
      <div className={css.searchRow}>
        <div className={cx(css.inputWrapper, isFocused && css.inputWrapperFocused)}>
          <IconSearch
            size={18}
            stroke={2}
            className={css.searchIcon}
          />
          <input
            ref={inputRef}
            type="search"
            className={css.input}
            placeholder={placeholder || t("gameSearchPlaceholder", {defaultValue: "Buscar juegos..."})}
            value={searchValue}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            aria-label={t("gameSearchAriaLabel", {defaultValue: "Buscar juegos"})}
          />
          {searchValue && (
            <button
              type="button"
              className={css.clearBtn}
              onClick={handleClear}
              aria-label={t("gameSearchClear", {defaultValue: "Limpiar búsqueda"})}
            >
              <IconX
                size={14}
                stroke={2.5}
              />
            </button>
          )}
          {isFocused && (
            <button
              type="button"
              className={cx(css.goBtn, searchValue.trim() && css.goBtnActive)}
              onMouseDown={e => {
                e.preventDefault();
                handleSubmit();
              }}
              aria-label={t("gameSearchSubmit", {defaultValue: "Buscar"})}
            >
              <IconArrowRight
                size={18}
                stroke={2.5}
              />
            </button>
          )}
        </div>

        {showFilters && (
          <button
            type="button"
            className={cx(css.filterToggle, filtersOpen && css.filterToggleActive)}
            onClick={() => setFiltersOpen(prev => !prev)}
            aria-label={t("gameSearchFilters", {defaultValue: "Filtros"})}
            aria-expanded={filtersOpen}
          >
            <IconAdjustmentsHorizontal
              size={20}
              stroke={2}
            />
            {hasActiveFilters && <span className={css.filterBadge} />}
          </button>
        )}
      </div>

      {/* Active filter tags */}
      {hasActiveFilters && !filtersOpen && (
        <div className={css.activeFilters}>
          {activeProvider && (
            <span className={css.activeTag}>
              {activeProvider}
              <button
                type="button"
                className={css.removeTag}
                onClick={() => onProviderChange?.("")}
                aria-label={t("gameSearchRemoveFilter", {defaultValue: "Quitar filtro"})}
              >
                <IconX
                  size={12}
                  stroke={2.5}
                />
              </button>
            </span>
          )}
          {activeCategory && (
            <span className={cx(css.activeTag, css.activeTagCategory)}>
              {activeCategory}
              <button
                type="button"
                className={css.removeTag}
                onClick={() => onCategoryChange?.("")}
                aria-label={t("gameSearchRemoveFilter", {defaultValue: "Quitar filtro"})}
              >
                <IconX
                  size={12}
                  stroke={2.5}
                />
              </button>
            </span>
          )}
          <button
            type="button"
            className={css.clearAll}
            onClick={handleClearAll}
          >
            {t("gameSearchClearFilters", {defaultValue: "Limpiar filtros"})}
          </button>
        </div>
      )}

      {/* Filter panels */}
      {filtersOpen && showFilters && (
        <div className={css.filtersPanel}>
          {!hideProviderFilter && normalizedProviders.length > 0 && (
            <div className={cx(css.filterGroup, css.providerGroup)}>
              <span className={css.filterLabel}>
                <span className={css.labelIcon}>
                  <IconBuildingStore
                    size={14}
                    stroke={2}
                  />
                </span>
                {t("gameSearchProviders", {defaultValue: "Proveedores"})}
              </span>
              <div className={css.providerGrid}>
                {normalizedProviders.map(opt => {
                  const avatar = getProviderLogo(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      className={cx(css.providerCard, activeProvider === opt.value && css.providerCardActive)}
                      onClick={() => handleProviderClick(opt.value)}
                    >
                      {avatar ? (
                        <img
                          src={avatar}
                          alt={opt.label}
                          className={css.providerCardAvatar}
                        />
                      ) : (
                        <span className={css.providerCardFallback}>{opt.label.charAt(0).toUpperCase()}</span>
                      )}
                      <span className={css.providerCardName}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {normalizedCategories.length > 0 && (
            <div className={cx(css.filterGroup, css.categoryGroup)}>
              <span className={css.filterLabel}>
                <span className={css.labelIcon}>
                  <IconCategory
                    size={14}
                    stroke={2}
                  />
                </span>
                {t("gameSearchCategories", {defaultValue: "Categorías"})}
              </span>
              <div className={css.chipsRow}>
                {normalizedCategories.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cx(css.chip, activeCategory === opt.value && css.chipActive)}
                    onClick={() => handleCategoryClick(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Result count */}
      {resultCount !== undefined && searchValue && (
        <span className={css.resultCount}>
          {resultCount} {t("gameSearchResults", {defaultValue: "resultados"})}
        </span>
      )}
    </div>
  );
};

export default GameSearchBar;
